import React, { useEffect, useState } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { Badge, Button, Card, Col, Row } from "reactstrap";

import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import QuestionerSectionForm from "./QuestionerSectionForm";
import {
  CloseCircleFilled,
  CloseCircleOutlined,
  PlusCircleFilled,
} from "@ant-design/icons";
import { useFieldArray, useFormContext } from "react-hook-form";
import Swal from "sweetalert2";

type Section = {
  id: string;
  name: string;
  questions: Question[];
  isCustom?: boolean;
};
type Question = {
  id: string;
  questioner: string;
  questionWeightage: string;
  description: string;
  questionType: "text" | "number" | "dropdown" | "date" | "checkbox" | "radio";
  options?: string[]; // For dropdowns
};
const Questioner: React.FC<any> = ({ prev, next }) => {
  const [sm, setSm] = useState(false);
  const { control, setValue, getValues, watch } = useFormContext();
  const { fields, append, remove }: any = useFieldArray({
    control,
    name: "questionerSections",
  });
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState<any>();

  const [newSectionName, setNewSectionName] = useState<string>("");
  const [showNewSectionInput, setShowNewSectionInput] =
    useState<boolean>(false);

  // Watch all sections to compute total section weightage
  const watchedSections = watch("questionerSections") || [];
  const totalSectionWeightage = watchedSections.reduce(
    (sum: number, s: any) => sum + (parseFloat(s?.sectionWeightage) || 0),
    0
  );
  const isSectionWeightageFull = totalSectionWeightage >= 100;

  // Validate that all questions in a section sum to 100 weightage
  const validateSectionQuestionWeightage = (sectionIdx: number): boolean => {
    const sections = getValues("questionerSections") || [];
    const section = sections[sectionIdx];
    if (!section) return true;
    const questions = section.questions || [];
    if (questions.length === 0) return true;
    const totalQuestionWeightage = questions.reduce(
      (sum: number, q: any) =>
        sum + (parseFloat(String(q?.questionWeightage || "0").replace(/[^0-9.]/g, "")) || 0),
      0
    );
    return totalQuestionWeightage === 100;
  };

  // Get current active section index
  const getActiveSectionIndex = (): number => {
    return fields.findIndex((s: any) => s.id === activeInvoice);
  };

  // Handle section tab switch with validation
  const handleSectionSwitch = (targetSectionId: string) => {
    const currentIndex = getActiveSectionIndex();
    if (currentIndex !== -1) {
      const sections = getValues("questionerSections") || [];
      const currentSection = sections[currentIndex];
      const questions = currentSection?.questions || [];
      if (questions.length > 0 && !validateSectionQuestionWeightage(currentIndex)) {
        Swal.fire(
          "Weightage Incomplete",
          `All questions weightage in "${currentSection.name}" section must total 100% before switching to another section.`,
          "warning"
        );
        return;
      }
    }
    setActiveInvoice(targetSectionId);
  };

  useEffect(() => {
    if (fields.length > 0 && !activeInvoice) {
      setActiveInvoice(fields[0].id); // Set to the first section dynamically
    }
  }, [fields, activeInvoice]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const isActive = (section: any): string => {
    return activeInvoice == section ? "active" : "";
  };

  const handleAddSection = () => {
    if (!newSectionName.trim()) return;
    const newSection: Section = {
      id: `custom-${Date.now()}`,
      name: newSectionName.trim(),
      questions: [],
      isCustom: true,
    };
    append(newSection);
    setNewSectionName("");
    setShowNewSectionInput(false);
  };

  const handleCancelAddSection = () => {
    setNewSectionName(""); // Clear the input field
    setShowNewSectionInput(false); // Hide the input field
  };

  return (
    <>
      <Head title="Questioner Section" />
      <Content>
        <Card
          className="card-bordered"
          style={{
            marginTop: "-35px",
            marginLeft: "-26px",
            marginRight: "-26px",
          }}
        >
          <div className="card-aside-wrap">
            <div
              className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg 
                ${sm ? "content-active" : ""}
              `}
              style={{ minHeight: "65vh" }}
            >
              <div className="card-inner-group">
                {/* <div className="card-inner"> */}
                {/* <div className="user-account-info py-0">
                    <h6 className="overline-title-alt" style={{ fontSize: 14 }}>
                      Questioners
                    </h6>
                  </div> */}
                {/* </div> */}
                <div className="card-inner p-0">
                  <ul className="link-list-menu">
                    {fields.map((item: any) => {
                      return (
                        <li
                          key={item.id}
                          onClick={() => {
                            handleSectionSwitch(item.id);
                          }}
                        >
                          <Link to="" className={isActive(item.id)}>
                            <span>{item.name}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                  <div style={{ padding: "1rem" }}>
                    {!showNewSectionInput ? (
                      <Button
                        size="sm"
                        color="primary"
                        onClick={() => setShowNewSectionInput(true)}
                        disabled={isSectionWeightageFull}
                        title={
                          isSectionWeightageFull
                            ? "Total section weightage has reached 100%"
                            : "Add a new section"
                        }
                      >
                        + Add New Section
                      </Button>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter new section name"
                          value={newSectionName}
                          onChange={(e) => setNewSectionName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleAddSection();
                            }
                          }}
                          autoFocus // Focus on the input when it appears
                          style={{ flexGrow: 1 }}
                        />

                        <PlusCircleFilled
                          onClick={handleAddSection}
                          style={{
                            fontSize: "24px",
                            color: newSectionName.trim()
                              ? "#28a745"
                              : "#b0b0b0",
                            cursor: newSectionName.trim()
                              ? "pointer"
                              : "not-allowed",
                            opacity: newSectionName.trim() ? 1 : 0.5,
                          }}
                          title="Add Section"
                        />

                        <CloseCircleFilled
                          onClick={handleCancelAddSection}
                          style={{
                            fontSize: "24px",
                            color: "#dc3545",
                            cursor: "pointer",
                          }}
                          title="Cancel"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {activeInvoice && (() => {
              const sectionIndex = fields.findIndex((s: any) => s.id === activeInvoice);
              return sectionIndex !== -1 ? (
                <QuestionerSectionForm
                  key={activeInvoice}
                  sectionIndex={sectionIndex}
                />
              ) : null;
            })()}
          </div>
        </Card>
        <div className="actions clearfix">
          <ul>
            <li>
              <Button
                color="primary"
                type="submit"
                onClick={() => {
                  const sections = getValues("questionerSections") || [];
                  // Only validate sections that have weightage > 0
                  const activeSections = sections.filter(
                    (s: any) => (parseFloat(s?.sectionWeightage) || 0) > 0
                  );
                  // Validate overall section weightage total = 100 (only active sections)
                  const totalSectionWeightage = activeSections.reduce(
                    (sum: number, s: any) =>
                      sum + (parseFloat(s?.sectionWeightage) || 0),
                    0
                  );
                  if (activeSections.length > 0 && totalSectionWeightage !== 100) {
                    Swal.fire(
                      "Section Weightage Incomplete",
                      `Total of all section weightages must equal 100%. Currently it is ${totalSectionWeightage}%.`,
                      "warning"
                    );
                    return;
                  }
                  // Validate question weightage per active section
                  const sectionsWithNoQuestions = activeSections.filter(
                    (s: any) => !s?.questions || s.questions.length === 0
                  );
                  if (sectionsWithNoQuestions.length > 0) {
                    const names = sectionsWithNoQuestions
                      .map((s: any) => s.name)
                      .join(", ");
                    Swal.fire(
                      "Questions Required",
                      `Please add at least one question in the following sections: ${names}`,
                      "warning"
                    );
                    return;
                  }
                  const sectionsWithInvalidQuestionWeightage = activeSections.filter(
                    (s: any) => {
                      const questions = s?.questions || [];
                      const total = questions.reduce(
                        (sum: number, q: any) =>
                          sum +
                          (parseFloat(
                            String(q?.questionWeightage || "0").replace(
                              /[^0-9.]/g,
                              ""
                            )
                          ) || 0),
                        0
                      );
                      return total !== 100;
                    }
                  );
                  if (sectionsWithInvalidQuestionWeightage.length > 0) {
                    const names = sectionsWithInvalidQuestionWeightage
                      .map((s: any) => s.name)
                      .join(", ");
                    Swal.fire(
                      "Weightage Incomplete",
                      `All questions weightage must total 100% in the following sections: ${names}`,
                      "warning"
                    );
                    return;
                  }
                  const sectionsWithoutEvaluator = activeSections.filter(
                    (s: any) => !s.evaluators || s.evaluators.length === 0
                  );
                  if (sectionsWithoutEvaluator.length > 0) {
                    const names = sectionsWithoutEvaluator
                      .map((s: any) => s.name)
                      .join(", ");
                    Swal.fire(
                      "Validation Error",
                      `Please assign at least one evaluator for the following sections: ${names}`,
                      "warning"
                    );
                    return;
                  }
                  next();
                }}
              >
                Next
              </Button>
            </li>
            <li>
              <Button
                type="button"
                color="primary"
                onClick={prev}
                className="btn-dim btn btn-secondary"
              >
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </Content>
    </>
  );
};

export default Questioner;
